/**
 * C-GTCC-VIEW-HW-GW2 — the panels of the GTCC view "Gateways".
 *
 * One component per drawn element of `GTCC_REFERENCE_2026-09-01.html:772-805`,
 * so `GwView.tsx` keeps the data and nothing else:
 *
 *   - {@link GwEndpointsPanel} — panel "Gateway-Endpunkte" with the reference's
 *     five columns ENDPUNKT | STATUS | VERBINDUNGEN | PAKETE/MIN |
 *     RECONNECTS 24 H,
 *   - {@link GwRejectionsPanel} — panel "Abgelehnte Verbindungen (Rejections)"
 *     with ZEIT | IMEI | GRUND | AKTION,
 *   - {@link GwIngestPanel} — panel "Ingest /gpsdata (Legacy GT One)",
 *   - {@link GwParserHealthPanel} — panel "Parser-Gesundheit".
 *
 * The same three properties hold as in `HwPanels.tsx`, and they are why several
 * cells are more cautious than the reference's sample text:
 *   - no number is computed from another number and none is invented: a missing
 *     measurement renders {@link ControlNotMeasurable} with the API's own
 *     `reason`, never a zero (P8),
 *   - no age is derived from this browser's clock — a rejection carries
 *     absolute timestamps and is printed as an absolute time (P9),
 *   - a verb is only offered where a route exists. The reference's
 *     `[Rohpaket ansehen]` has no backend at all, so the AKTION cell says where
 *     a rejected device is decided instead of drawing a dead button (P10).
 */
import { EmptyState, ErrorState, Stack, StatePill } from "@genius-tracker/design-system";
import type { Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import { useState, type CSSProperties, type ReactNode } from "react";

import { formatTimestampSafe } from "../../../../locale-format.js";
import {
  GW_PLATFORM_SURFACE,
  GW_REQUIRED_ROLE,
  type GwEndpointDto,
  type GwEndpointsDto,
  type GwIngestCountsDto,
  type GwIngestDto,
  type GwParserHealthDto,
  type GwRejectionDto,
  type GwRejectionsDto,
  type GwRowDto,
  type GwUnknownDeviceDto,
} from "../../../api/gw.js";
import {
  ControlDot,
  ControlMono,
  ControlNote,
  ControlNotMeasurable,
  ControlPanel,
  ControlRow,
  ControlTableWrap,
  HOST_CELL_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
  asList,
  formatDecimal1,
  formatInteger,
  maskImei,
  measuredNumber,
  toneOfVerdict,
  type ControlTone,
} from "../../control/ControlPrimitives.js";

/* ---------------------------------------------------------------- polling */

export const GW_STALE_MS = 30_000;
export const GW_REFETCH_MS = 60_000;

export const GW_QUERY_OPTIONS = {
  staleTime: GW_STALE_MS,
  refetchInterval: GW_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

/** Same rule as `HwPanels.tsx`: named for assistive technology, drawn once. */
const CAPTION_STYLE: CSSProperties = {
  captionSide: "top",
  textAlign: "start",
  display: "none",
};

const NOWRAP_CELL_STYLE: CSSProperties = { ...TD_STYLE, whiteSpace: "nowrap" };

/* Der Grund-Filter der Vorgaengerin, in den Tokens dieses Decks. */
const FILTER_LABEL_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
};

const FILTER_TEXT_STYLE: CSSProperties = {
  fontSize: "var(--gt-cmp-gtcc-table-head-size)",
  letterSpacing: "0.1em",
  color: "var(--gt-sys-text-muted)",
  textTransform: "uppercase",
};

const FILTER_SELECT_STYLE: CSSProperties = {
  fontSize: "var(--gt-cmp-gtcc-table-size)",
  color: "var(--gt-sys-text)",
  background: "var(--gt-sys-surface-raised)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-md)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-2)",
  minWidth: 0,
};

const STATUS_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
};

/* ------------------------------------------------- 0 · the named denial */

/**
 * E-GTCC-GW-WRITE-PORT1 — a refused source says WHICH authorization is missing.
 *
 * `GwView` only ever named a denial when EVERY source refused, and then only as
 * "Zugriff verweigert". A reader who sees three panels and one silent gap learns
 * nothing about why. The two `/v1/admin/gateway/**` routes are behind
 * `requirePlatformOwner("gateway")`, so the missing authorization has a name —
 * the role `platform.owner` on the platform surface `gateway` — and this notice
 * prints it in place of the panel's body instead of an empty table or a
 * "nicht messbar" that would read like a broken measurement.
 */
export function GwDeniedNotice(props: { testId: string }): ReactNode {
  const { t } = useLocale();
  return (
    <div data-testid={props.testId}>
      <ErrorState
        severity="warn"
        title={t("admin.gtcc.gw.denied.title", { role: GW_REQUIRED_ROLE })}
        body={t("admin.gtcc.gw.denied.body", {
          role: GW_REQUIRED_ROLE,
          surface: GW_PLATFORM_SURFACE,
        })}
      />
    </div>
  );
}

/* ---------------------------------------------------- 0 · measured rows */

/**
 * Row labels come from this catalog, not from the payload: the API's `label` is
 * one German string for every reader, and this deck answers in four languages.
 * A key the catalog does not know falls back to the payload's own label, which
 * is worse than a translation and better than an empty cell.
 */
const GW_ROW_LABEL_KEYS: Readonly<Record<string, string>> = {
  endpoints_configured: "admin.gtcc.gw.row.endpoints_configured",
  connections_active: "admin.gtcc.gw.row.connections_active",
  packets_per_min: "admin.gtcc.gw.row.packets_per_min",
  reconnects_24h: "admin.gtcc.gw.row.reconnects_24h",
  dropped_share_24h: "admin.gtcc.gw.row.dropped_share_24h",
  unknown_imei_24h: "admin.gtcc.gw.row.unknown_imei_24h",
  device_clock_skew_max: "admin.gtcc.gw.row.device_clock_skew_max",
  clock_skew_drop_bound: "admin.gtcc.gw.row.clock_skew_drop_bound",
};

/** One measured row of an envelope, with its verdict as the row's tone. */
export function GwRow(props: { row: GwRowDto }): ReactNode {
  const { t } = useLocale();
  const { row } = props;
  const labelKey = GW_ROW_LABEL_KEYS[row.key];
  const tone: ControlTone = toneOfVerdict(row.verdict);

  return (
    <ControlRow
      testId={`gtcc-gw-row-${row.key}`}
      tone={tone}
      label={labelKey === undefined ? row.label : t(labelKey)}
      value={
        row.verdict === "unmeasurable" ? (
          <ControlNotMeasurable
            label={
              row.reason === undefined
                ? t("admin.gtcc.gw.not_measurable")
                : t(`admin.gtcc.gw.reason.${row.reason}`)
            }
          />
        ) : (
          <ControlMono tone={tone}>{row.valueText}</ControlMono>
        )
      }
    />
  );
}

/* ------------------------------------------------ 1 · Gateway-Endpunkte */

/** A number the endpoint could not be attributed, printed as such. */
function EndpointNumber(props: {
  value: number | null;
  reason: string | null;
  locale: Locale;
  decimals?: 0 | 1;
}): ReactNode {
  const { t } = useLocale();
  const measured = measuredNumber(props.value);

  if (measured === null) {
    return (
      <ControlNotMeasurable
        label={
          props.reason === null
            ? t("admin.gtcc.gw.not_measurable")
            : t(`admin.gtcc.gw.reason.${props.reason}`)
        }
      />
    );
  }
  return (
    <ControlMono>
      {props.decimals === 1
        ? formatDecimal1(measured, props.locale)
        : formatInteger(measured, props.locale)}
    </ControlMono>
  );
}

/**
 * The reference's endpoint table.
 *
 * The rows are the listeners the API process is configured with — not a list
 * from the design sheet: the reference draws `codec8-main :5027` and
 * `codec8-fallback :5028`, and this deployment may run neither. The STATUS cell
 * follows the endpoint's own verdict, so a configured listener whose traffic
 * cannot be attributed reads "nicht messbar" and not "Up".
 */
export function GwEndpointsPanel(props: {
  endpoints: GwEndpointsDto | null;
  locale: Locale;
}): ReactNode {
  const { t } = useLocale();
  const endpoints = asList<GwEndpointDto>(props.endpoints?.endpoints);

  return (
    <ControlPanel testId="gtcc-gw-endpoints" title={t("admin.gtcc.gw.endpoints.title")}>
      {endpoints.length === 0 ? (
        <div data-testid="gtcc-gw-endpoints-empty">
          <EmptyState
            title={t("admin.gtcc.gw.endpoints.empty.title")}
            body={t("admin.gtcc.gw.endpoints.empty.body")}
          />
        </div>
      ) : (
        <ControlTableWrap>
          <table style={TABLE_STYLE} data-testid="gtcc-gw-endpoints-table">
            <caption style={CAPTION_STYLE}>{t("admin.gtcc.gw.endpoints.caption")}</caption>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.endpoints.col.endpoint")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.endpoints.col.status")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.endpoints.col.connections")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.endpoints.col.packets")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.endpoints.col.reconnects")}
                </th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((endpoint) => {
                const tone = toneOfVerdict(endpoint.verdict);
                return (
                  <tr key={endpoint.key} data-testid="gtcc-gw-endpoints-row" data-tone={tone}>
                    <td style={HOST_CELL_STYLE}>{`${endpoint.label} ${endpoint.address}`}</td>
                    <td style={STATUS_CELL_STYLE}>
                      <ControlDot tone={tone} />
                      {endpoint.verdict === "unmeasurable" ? (
                        <ControlNotMeasurable label={t("admin.gtcc.gw.not_measurable")} />
                      ) : (
                        <ControlMono tone={tone}>{t("admin.gtcc.gw.endpoints.up")}</ControlMono>
                      )}
                    </td>
                    <td style={NOWRAP_CELL_STYLE}>
                      <EndpointNumber
                        value={endpoint.connectionsActive}
                        reason={endpoint.reason}
                        locale={props.locale}
                      />
                    </td>
                    <td style={NOWRAP_CELL_STYLE}>
                      <EndpointNumber
                        value={endpoint.packetsPerMin}
                        reason={endpoint.reason}
                        locale={props.locale}
                        decimals={1}
                      />
                    </td>
                    <td style={NOWRAP_CELL_STYLE}>
                      <EndpointNumber
                        value={endpoint.reconnects24h}
                        reason={endpoint.reason}
                        locale={props.locale}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ControlTableWrap>
      )}
    </ControlPanel>
  );
}

/* ------------------------------------- 2 · Abgelehnte Verbindungen */

/**
 * The rejection reasons the gateway diagnostics service can emit
 * (`admin/gateway-diagnostics.service.ts`). A reason outside this catalog is
 * printed raw rather than translated into something it does not say.
 */
const GW_REJECTION_REASON_KEYS: Readonly<Record<string, string>> = {
  unknown: "admin.gtcc.gw.rejections.reason.unknown",
  disabled: "admin.gtcc.gw.rejections.reason.disabled",
  unassigned: "admin.gtcc.gw.rejections.reason.unassigned",
  device_pending_approval: "admin.gtcc.gw.rejections.reason.device_pending_approval",
  device_rejected: "admin.gtcc.gw.rejections.reason.device_rejected",
  device_suspended: "admin.gtcc.gw.rejections.reason.device_suspended",
  device_archived: "admin.gtcc.gw.rejections.reason.device_archived",
  device_not_active: "admin.gtcc.gw.rejections.reason.device_not_active",
};

/** Only a device waiting for approval has somewhere for the reader to go. */
const DECIDABLE_REASONS: ReadonlySet<string> = new Set(["device_pending_approval", "unassigned"]);

const REJECTION_TONE: Readonly<Record<string, ControlTone>> = {
  device_pending_approval: "warn",
  unassigned: "warn",
  unknown: "crit",
  disabled: "crit",
  device_rejected: "crit",
  device_suspended: "crit",
  device_archived: "muted",
  device_not_active: "crit",
};

/**
 * The reference's rejection list, with the window it was actually read over in
 * the heading. ZEIT is `lastSeenAt` as an absolute time: the reference writes
 * `13:4x`, which is an age, and an age would have to come from this browser's
 * clock — a wrong clock would then make an old rejection look current (P9).
 *
 * E-GTCC-GW-WRITE-PORT1 ports the reason filter of `GtccGatewayView.tsx` back in
 * (its `<select aria-label>` over `reasonFilter`). It filters the rows the route
 * already delivered — it does NOT re-read with a server-side filter, because the
 * route has no reason parameter; and it never hides the fact that the window
 * itself was quiet, which is a different statement from "no row with this
 * reason".
 */
/**
 * The value of the filter's "all" option. Not a reason the API can emit, so no
 * payload reason can ever collide with it.
 */
const GW_REJECTION_FILTER_ALL = "__all__";

export function GwRejectionsPanel(props: {
  rejections: GwRejectionsDto | null;
  locale: Locale;
  denied?: boolean;
}): ReactNode {
  const { t, locale } = useLocale();
  const [selectedReason, setSelectedReason] = useState<string>(GW_REJECTION_FILTER_ALL);
  const all = asList<GwRejectionDto>(props.rejections?.recent);
  const counts = props.rejections?.counts ?? {};
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  });

  /* E-GTCC-GW-WRITE-PORT1 — die Auswahl der Vorgaengerin: die Vereinigung der
     gezaehlten Gruende und der Gruende der gelisteten Ablehnungen, sortiert.
     Beides steht in der Antwort; nichts davon wird hier ausgedacht.
     Ein Grund, den die Antwort nicht traegt, ist kein Filter: eine Zeile ohne
     `reason` wuerde sonst eine Option ohne Wert und ohne Schluessel erzeugen
     (Q22-Fall `id-only`), und React meldete das als Konsolenfehler. */
  const reasons = [
    ...new Set(
      [...Object.keys(counts), ...all.map((row) => row.reason)].filter(
        (reason): reason is string => typeof reason === "string" && reason.length > 0,
      ),
    ),
  ].sort();
  const recent =
    selectedReason === GW_REJECTION_FILTER_ALL
      ? all
      : all.filter((row) => row.reason === selectedReason);

  const reasonLabel = (reason: string): string => {
    const key = GW_REJECTION_REASON_KEYS[reason];
    return key === undefined ? reason : t(key);
  };

  const filter =
    reasons.length === 0 ? null : (
      <label style={FILTER_LABEL_STYLE} data-testid="gtcc-gw-rejections-filter">
        <span style={FILTER_TEXT_STYLE}>{t("admin.gtcc.gw.rejections.filter.label")}</span>
        <select
          aria-label={t("admin.gtcc.gw.rejections.filter.label")}
          value={selectedReason}
          onChange={(event) => setSelectedReason(event.target.value)}
          style={FILTER_SELECT_STYLE}
        >
          <option value={GW_REJECTION_FILTER_ALL}>
            {t("admin.gtcc.gw.rejections.filter.all")}
          </option>
          {reasons.map((reason) => {
            /* Die Zahl steht in `counts` der Antwort oder gar nicht — gezaehlt
               wird hier nichts. */
            const count: number | undefined = counts[reason];
            return (
              <option key={reason} value={reason}>
                {count === undefined
                  ? reasonLabel(reason)
                  : t("admin.gtcc.gw.rejections.filter.option", {
                      reason: reasonLabel(reason),
                      count: formatInteger(count, props.locale),
                    })}
              </option>
            );
          })}
        </select>
      </label>
    );

  if (props.denied === true) {
    return (
      <ControlPanel testId="gtcc-gw-rejections" title={t("admin.gtcc.gw.rejections.title")}>
        <GwDeniedNotice testId="gtcc-gw-rejections-denied" />
      </ControlPanel>
    );
  }

  return (
    <ControlPanel testId="gtcc-gw-rejections" title={t("admin.gtcc.gw.rejections.title")}>
      {filter}
      {all.length > 0 && recent.length === 0 ? (
        <div data-testid="gtcc-gw-rejections-filter-empty">
          <EmptyState
            title={t("admin.gtcc.gw.rejections.filter.empty.title")}
            body={t("admin.gtcc.gw.rejections.filter.empty.body", {
              reason: reasonLabel(selectedReason),
            })}
          />
        </div>
      ) : recent.length === 0 ? (
        <div data-testid="gtcc-gw-rejections-empty">
          <EmptyState
            title={t("admin.gtcc.gw.rejections.empty.title")}
            body={t("admin.gtcc.gw.rejections.empty.body")}
          />
        </div>
      ) : (
        <ControlTableWrap>
          <table style={TABLE_STYLE} data-testid="gtcc-gw-rejections-table">
            <caption style={CAPTION_STYLE}>{t("admin.gtcc.gw.rejections.caption")}</caption>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.rejections.col.time")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.rejections.col.imei")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.rejections.col.reason")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.gw.rejections.col.action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {recent.map((rejection) => {
                const tone = REJECTION_TONE[rejection.reason] ?? "warn";
                const reasonKey = GW_REJECTION_REASON_KEYS[rejection.reason];
                return (
                  <tr key={rejection.id} data-testid="gtcc-gw-rejections-row" data-tone={tone}>
                    <td style={NOWRAP_CELL_STYLE}>
                      <time dateTime={rejection.lastSeenAt}>
                        {formatTimestampSafe(rejection.lastSeenAt, (date) =>
                          timeFormatter.format(date),
                        )}
                      </time>
                    </td>
                    <td style={HOST_CELL_STYLE}>{maskImei(rejection.imei)}</td>
                    <td style={TD_STYLE}>
                      <ControlMono tone={tone}>
                        {reasonKey === undefined ? rejection.reason : t(reasonKey)}
                      </ControlMono>
                    </td>
                    <td style={TD_STYLE}>
                      {/* Kein toter Knopf: der Verweis nennt den Ort, an dem
                          dieses Geraet entschieden wird — sonst nichts. */}
                      {DECIDABLE_REASONS.has(rejection.reason) ? (
                        <ControlMono>{t("admin.gtcc.gw.rejections.action.queue")}</ControlMono>
                      ) : (
                        <ControlMono tone="muted">
                          {t("admin.gtcc.gw.rejections.action.none")}
                        </ControlMono>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ControlTableWrap>
      )}
      {/* PAKET 003240 — the read behind this table is capped; when the API
          reports `truncated`, the panel says the window holds more than it
          shows instead of letting a full page look like the whole window.
          The counts above stay complete — they are aggregated in SQL. */}
      {props.rejections?.truncated === true ? (
        <StatePill
          tone="neutral"
          label={t("admin.gtcc.gw.rejections.truncated")}
          dataTestId="gtcc-gw-rejections-truncated"
        />
      ) : null}
    </ControlPanel>
  );
}

/* --------------------------------------------- 3 · Ingest /gpsdata */

const INGEST_STATUS_TONE: Readonly<Record<string, ControlTone>> = {
  ok: "ok",
  near_limit: "warn",
  limited: "crit",
};

/**
 * The reference's four `.row`s of the legacy ingest panel. The heading takes
 * no `note` (the route lived there before D-GTCC-VIEW-TRIAGE1 gw-2; the
 * reference shows nothing next to the title).
 *
 * "Throttling aktiv (240 Burst / 480 pro min)" is the configured budget, so it
 * is read from `limits` and not repeated from the sheet; with the limiter off
 * the row says that instead of printing numbers that do not apply. The counters
 * are the route's own 1 h window — none of them is summed here.
 *
 * The last row is the reference's literal legend for the recent-events table
 * that lives further down this view (`gtcc-gateway-recent-table`), not a
 * live count (D-GTCC-VIEW-TRIAGE1 gw-4).
 */
export function GwIngestPanel(props: {
  ingest: GwIngestDto | null;
  locale: Locale;
  denied?: boolean;
}): ReactNode {
  const { t } = useLocale();
  const ingest = props.ingest;
  /* Eine 200 ohne `windows` ist kein gemessenes Fenster, sondern keins. */
  const lastHour = ingest?.windows?.["1h"];
  const limits = ingest?.limits ?? null;
  const forwarders = asList(ingest?.forwarders);

  const notMeasurable = <ControlNotMeasurable label={t("admin.gtcc.gw.not_measurable")} />;

  if (props.denied === true) {
    return (
      <ControlPanel testId="gtcc-gw-ingest" title={t("admin.gtcc.gw.ingest.title")}>
        <GwDeniedNotice testId="gtcc-gw-ingest-denied" />
      </ControlPanel>
    );
  }

  return (
    <ControlPanel testId="gtcc-gw-ingest" title={t("admin.gtcc.gw.ingest.title")}>
      <ControlRow
        testId="gtcc-gw-ingest-status"
        tone={ingest === null ? "muted" : (INGEST_STATUS_TONE[ingest.status] ?? "muted")}
        label={t("admin.gtcc.gw.ingest.status")}
        value={
          ingest === null ? (
            notMeasurable
          ) : (
            <ControlMono tone={INGEST_STATUS_TONE[ingest.status] ?? "muted"}>
              {limits === null || !limits.enabled
                ? t(`admin.gtcc.gw.ingest.state.${ingest.status}`)
                : t("admin.gtcc.gw.ingest.state_throttled", {
                    state: t(`admin.gtcc.gw.ingest.state.${ingest.status}`),
                    burst: formatInteger(limits.ip.burst, props.locale),
                    perMin: formatInteger(limits.ip.sustainedPerMin, props.locale),
                  })}
            </ControlMono>
          )
        }
      />
      <ControlRow
        testId="gtcc-gw-ingest-last-hour"
        tone={lastHour === undefined ? "muted" : lastHour.rateLimited > 0 ? "warn" : "ok"}
        label={t("admin.gtcc.gw.ingest.last_hour")}
        value={
          lastHour === undefined ? (
            notMeasurable
          ) : (
            <ControlMono>
              {t("admin.gtcc.gw.ingest.last_hour_value", {
                accepted: formatInteger(lastHour.accepted, props.locale),
                rateLimited: formatInteger(lastHour.rateLimited, props.locale),
                invalid: formatInteger(lastHour.invalidPayload, props.locale),
                unknown: formatInteger(lastHour.unknownDevice, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      <ControlRow
        testId="gtcc-gw-ingest-forwarders"
        tone={
          ingest === null
            ? "muted"
            : forwarders.length > 0 && forwarders.every((forwarder) => forwarder.trustedForwarder)
              ? "ok"
              : "warn"
        }
        label={t("admin.gtcc.gw.ingest.forwarders")}
        value={
          ingest === null ? (
            notMeasurable
          ) : (
            <ControlMono>
              {t("admin.gtcc.gw.ingest.forwarders_value", {
                trusted: formatInteger(
                  forwarders.filter((forwarder) => forwarder.trustedForwarder).length,
                  props.locale,
                ),
                total: formatInteger(forwarders.length, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      {ingest?.forwardersTruncated === true ? (
        <ControlNote testId="gtcc-gw-ingest-forwarders-truncated">
          {t("admin.gtcc.gw.ingest.forwarders_truncated")}
        </ControlNote>
      ) : null}
      <ControlRow
        testId="gtcc-gw-ingest-recent"
        tone={ingest === null ? "muted" : "info"}
        label={t("admin.gtcc.gw.ingest.recent")}
        value={
          ingest === null ? (
            notMeasurable
          ) : (
            <ControlMono>{t("admin.gtcc.gw.ingest.recent_summary")}</ControlMono>
          )
        }
      />
      <GwIngestWindows windows={ingest?.windows} locale={props.locale} />
    </ControlPanel>
  );
}

/* ------------------------------------------- 3a · die drei Ingest-Fenster */

/**
 * E-GTCC-GW-WRITE-PORT1 — the three windows the predecessor drew.
 *
 * `GwIngestPanel` reads `windows["1h"]` alone, so 5 min and 24 h — both in every
 * payload of this route — were unreachable after the view rebuild. The
 * predecessor showed all three (`GtccGatewayView.tsx`, `INGEST_WINDOWS` over
 * `admin.gateway_ingest.window.*` × `admin.gateway_ingest.metric.*`), and these
 * are that table's labels verbatim, not a new vocabulary.
 *
 * Every cell is a counter of the payload. Nothing is summed, averaged or
 * carried over between windows: an absent `windows` object is one "nicht
 * messbar", not twelve zeroes.
 */
const GW_INGEST_WINDOW_KEYS = ["5m", "1h", "24h"] as const;

type GwIngestWindowKey = (typeof GW_INGEST_WINDOW_KEYS)[number];

const GW_INGEST_METRICS: ReadonlyArray<{
  readonly key: keyof GwIngestCountsDto;
  readonly labelKey: string;
}> = [
  { key: "accepted", labelKey: "admin.gateway_ingest.metric.accepted" },
  { key: "rateLimited", labelKey: "admin.gateway_ingest.metric.rate_limited" },
  { key: "invalidPayload", labelKey: "admin.gateway_ingest.metric.invalid_payload" },
  { key: "unknownDevice", labelKey: "admin.gateway_ingest.metric.unknown_device" },
];

export function GwIngestWindows(props: {
  windows: GwIngestDto["windows"] | undefined;
  locale: Locale;
}): ReactNode {
  const { t } = useLocale();
  const windows = props.windows;

  if (windows === undefined) {
    return (
      <ControlRow
        testId="gtcc-gw-ingest-windows-unmeasurable"
        tone="muted"
        label={t("admin.gtcc.gw.ingest.windows.title")}
        value={<ControlNotMeasurable label={t("admin.gtcc.gw.not_measurable")} />}
      />
    );
  }

  return (
    <ControlTableWrap>
      <table style={TABLE_STYLE} data-testid="gtcc-gw-ingest-windows">
        <caption style={CAPTION_STYLE}>{t("admin.gtcc.gw.ingest.windows.caption")}</caption>
        <thead>
          <tr>
            <th scope="col" style={TH_STYLE}>
              {t("admin.gtcc.gw.ingest.windows.col.window")}
            </th>
            {GW_INGEST_METRICS.map((metric) => (
              <th key={metric.key} scope="col" style={TH_STYLE}>
                {t(metric.labelKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {GW_INGEST_WINDOW_KEYS.map((windowKey: GwIngestWindowKey) => {
            const counts: GwIngestCountsDto | undefined = windows[windowKey];
            return (
              <tr key={windowKey} data-testid={`gtcc-gw-ingest-window-${windowKey}`}>
                {/* Wie in jeder GTCC-Tabelle bleibt die erste Zelle eine
                    Datenzelle: `gtcc-table-semantics` verlangt genau null
                    Zeilenkoepfe im ganzen Deck. */}
                <td style={HOST_CELL_STYLE}>{t(`admin.gateway_ingest.window.${windowKey}`)}</td>
                {GW_INGEST_METRICS.map((metric) => (
                  <td key={metric.key} style={NOWRAP_CELL_STYLE}>
                    {counts === undefined ? (
                      <ControlNotMeasurable label={t("admin.gtcc.gw.not_measurable")} />
                    ) : (
                      <ControlMono tone={metric.key === "accepted" ? undefined : "muted"}>
                        {formatInteger(counts[metric.key], props.locale)}
                      </ControlMono>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ControlTableWrap>
  );
}

/* ------------------------------------------ 3b · Unbekannte Geraete */

/**
 * E-GTCC-GW-WRITE-PORT1 — the card `gtcc-gw-unknown` of
 * `GtccGatewayView.tsx:865`, ported, but fed from the real source.
 *
 * The predecessor aggregated this list in the browser from the ring buffer
 * (`aggregateUnknownDevices`, same file `:200`), so its numbers only ever
 * covered the last events the snapshot happened to carry. The route has the
 * durable counter: `gateway-ingest-diagnostics.routes.ts:84` calls
 * `unknownDevices()` and `:85` returns it as the payload field `unknownDevices`,
 * read from the table of migration 0154 — top 50 by attempt count
 * (`gpsdata-legacy/unknown-devices.repo.ts:28`). Nothing in here is counted,
 * deduplicated or derived by this component.
 *
 * Three distinct states, because the route's own docblock forbids collapsing
 * them: the field may be missing (`undefined` → nicht messbar, NOT "no unknown
 * device"), it may be an empty list (measured, nothing to report), or it carries
 * rows. A refusal names the missing role instead of any of the three.
 */
export function GwUnknownDevicesPanel(props: {
  ingest: GwIngestDto | null;
  locale: Locale;
  denied?: boolean;
}): ReactNode {
  const { t, locale } = useLocale();
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  });
  const unknownDevices = props.ingest?.unknownDevices;
  const rows: ReadonlyArray<GwUnknownDeviceDto> | undefined = unknownDevices?.rows;

  const body = (): ReactNode => {
    if (props.denied === true) return <GwDeniedNotice testId="gtcc-gw-unknown-denied" />;
    if (rows === undefined) {
      return (
        <ControlRow
          testId="gtcc-gw-unknown-unmeasurable"
          tone="muted"
          label={t("admin.gtcc.gw.unknown.counter")}
          value={<ControlNotMeasurable label={t("admin.gtcc.gw.unknown.not_read")} />}
        />
      );
    }
    if (rows.length === 0) {
      return (
        <div data-testid="gtcc-gw-unknown-empty">
          <EmptyState
            title={t("admin.gtcc.gw.unknown.empty.title")}
            body={t("admin.gtcc.gw.unknown.empty.body")}
          />
        </div>
      );
    }
    return (
      <ControlTableWrap>
        <table style={TABLE_STYLE} data-testid="gtcc-gw-unknown-table">
          <caption style={CAPTION_STYLE}>{t("admin.gtcc.gw.unknown.caption")}</caption>
          <thead>
            <tr>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.gw.unknown.col.identifier")}
              </th>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.gw.unknown.col.attempts")}
              </th>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.gw.unknown.col.first_seen")}
              </th>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.gw.unknown.col.last_seen")}
              </th>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.gw.unknown.col.source")}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Der Index gehoert in den Schluessel: eine Zeile ohne `deviceId`
                traegt sonst den Schluessel `undefined` und React meldet einen
                Konsolenfehler, statt die unvollstaendige Zeile zu zeigen. */}
            {rows.map((row, index) => (
              <tr
                key={`${index}-${row.deviceId}`}
                data-testid="gtcc-gw-unknown-row"
                data-tone="warn"
              >
                {/* Die Kennung ist maskiert wie in der Ablehnungsliste — eine
                    unbekannte IMEI ist genauso ein Geraetebezug wie eine
                    bekannte (D-GTCC-VIEW-TRIAGE1 gw-5). */}
                <td style={HOST_CELL_STYLE}>{maskImei(row.deviceId)}</td>
                <td style={NOWRAP_CELL_STYLE}>
                  <ControlMono tone="warn">{formatInteger(row.count, props.locale)}</ControlMono>
                </td>
                <td style={NOWRAP_CELL_STYLE}>
                  <time dateTime={row.firstSeen}>
                    {formatTimestampSafe(row.firstSeen, (date) => timeFormatter.format(date))}
                  </time>
                </td>
                <td style={NOWRAP_CELL_STYLE}>
                  <time dateTime={row.lastSeen}>
                    {formatTimestampSafe(row.lastSeen, (date) => timeFormatter.format(date))}
                  </time>
                </td>
                <td style={TD_STYLE}>
                  {row.lastSourceIp === null ? (
                    <ControlNotMeasurable label={t("admin.gtcc.gw.unknown.no_source")} />
                  ) : (
                    <ControlMono>
                      {row.lastPath === null
                        ? row.lastSourceIp
                        : `${row.lastSourceIp} · ${row.lastPath}`}
                    </ControlMono>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {unknownDevices?.truncated ? (
          <ControlNote testId="gtcc-gw-unknown-truncated">
            {t("admin.gtcc.gw.unknown.truncated")}
          </ControlNote>
        ) : null}
      </ControlTableWrap>
    );
  };

  return (
    <ControlPanel
      testId="gtcc-gw-unknown"
      title={t("admin.gtcc.gw.unknown.title")}
      note={t("admin.gtcc.gw.unknown.note")}
    >
      {body()}
    </ControlPanel>
  );
}

/* ------------------------------------------------ 4 · Parser-Gesundheit */

/**
 * The reference's three parser rows. Two are measured from the gateway
 * counters; the third — "Uhr-Abweichung Geräte max." — has no store behind it
 * and arrives as an unmeasurable row of the projection. Next to it stands the
 * bound the bridge actually drops future timestamps at, which is the fact that
 * does exist.
 */
export function GwParserHealthPanel(props: { health: GwParserHealthDto | null }): ReactNode {
  const { t } = useLocale();
  const rows = asList<GwRowDto>(props.health?.rows);

  return (
    <ControlPanel testId="gtcc-gw-parser" title={t("admin.gtcc.gw.parser.title")}>
      {rows.length === 0 ? (
        <div data-testid="gtcc-gw-parser-empty">
          <EmptyState
            title={t("admin.gtcc.gw.parser.empty.title")}
            body={t("admin.gtcc.gw.parser.empty.body")}
          />
        </div>
      ) : (
        <Stack gap={0}>
          {rows.map((row) => (
            <GwRow key={row.key} row={row} />
          ))}
        </Stack>
      )}
    </ControlPanel>
  );
}
